import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getArticles, getDestination } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import { Skeleton } from "../components/ui/skeleton";
import { MapPin, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function DestinationPage() {
  const { slug } = useParams();
  const [destination, setDestination] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadData = async () => {
    setLoading(true);
    try {
      const destRes = await getDestination(slug);
      setDestination(destRes.data);

      if (destRes.data) {
        const articlesRes = await getArticles({ destination: destRes.data.id, status: "published" });
        setArticles(articlesRes.data);
      }
    } catch (error) {
      console.error("Error loading destination:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="destination-page">
      {/* Hero */}
      <section className="relative h-[50vh] md:h-[60vh]">
        <div className="absolute inset-0 bg-primary">
          {destination?.image_url && (
            <img
              src={destination.image_url}
              alt={destination.name}
              className="w-full h-full object-cover opacity-50"
            />
          )}
        </div>
        <div className="absolute inset-0 hero-gradient" />
        
        <Link
          to="/"
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 text-white/80 hover:text-white transition-colors z-10"
        >
          <ArrowLeft className="h-5 w-5" />
          Accueil
        </Link>

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20">
          <div className="max-w-4xl flex items-end gap-6">
            <div className="hidden md:flex w-20 h-20 bg-accent rounded-sm items-center justify-center">
              <MapPin className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                {destination?.name || slug}
              </h1>
              {destination?.country && (
                <p className="text-white/80 text-lg">{destination.country}</p>
              )}
              {destination?.description && (
                <p className="text-white/70 mt-2 max-w-xl">{destination.description}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-muted-foreground mb-8">
            {loading ? "Chargement..." : `${articles.length} article${articles.length > 1 ? 's' : ''}`}
          </p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(6).fill(0).map((_, i) => (
                <div key={i}>
                  <Skeleton className="aspect-[4/3] mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {articles.map((article, index) => (
                <div 
                  key={article.id} 
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <MapPin className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-semibold text-primary mb-2">
                Aucun article pour cette destination
              </h3>
              <p className="text-muted-foreground">
                Revenez bientôt pour découvrir nos publications.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
